import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import type {TooltipProps} from 'recharts';
import type {
  Formatter,
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import {match, Pattern} from 'ts-pattern';

import type {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {ChartSwatch} from '../ChartSwatch/ChartSwatch';

const TOOLTIP_MIN_WIDTH = 32;

type TooltipItem = Payload<ValueType, NameType>;

// Recharts lets a formatter return the value alone or a [value, name] pair.
function isValueNamePair(
  formatted: ReactNode | [ReactNode, NameType]
): formatted is [ReactNode, NameType] {
  return Array.isArray(formatted);
}

function formatItem(
  item: TooltipItem,
  index: number,
  items: TooltipItem[],
  formatter: Formatter<ValueType, NameType> | undefined
) {
  return match({itemFormatter: item.formatter ?? formatter, value: item.value, name: item.name})
    .with(
      {itemFormatter: Pattern.nonNullable, value: Pattern.nonNullable, name: Pattern.nonNullable},
      (formattable) => {
        const formatted = formattable.itemFormatter(
          formattable.value,
          formattable.name,
          item,
          index,
          items
        );
        return isValueNamePair(formatted)
          ? {name: formatted[1], value: formatted[0]}
          : {name: formattable.name, value: formatted};
      }
    )
    .otherwise(() => ({name: item.name, value: item.value}));
}

// Recharts gives a bar coloured per data entry, for example by a `Cell`, the colour of its series
// in `item.color`. Such a chart passes the colour of each bar as `fill` in the data entry.
function swatchColorOf(item: TooltipItem) {
  const entry: unknown = item.payload;
  return typeof entry === 'object' &&
    isNotNil(entry) &&
    'fill' in entry &&
    typeof entry.fill === 'string'
    ? entry.fill
    : item.color;
}

/**
 * Tooltip in the shared chart look. Pass it as `content` of a Recharts `Tooltip`. The
 * `formatter` and `labelFormatter` of that `Tooltip` work as they do in the default tooltip. A
 * data entry with a `fill` colour gives the swatch the colour of its own bar.
 */
export function ChartTooltipContent(props: TooltipProps<ValueType, NameType>) {
  const items = (props.payload ?? []).filter((item) => item.type !== 'none');
  if (props.active !== true || items.length === 0) {
    return null;
  }

  const label =
    isNotNil(props.labelFormatter) && isNotNil(props.label)
      ? props.labelFormatter(props.label, items)
      : props.label;

  return (
    <Box
      backgroundColor="general.white"
      border="1px solid"
      borderColor="palettes.neutral.30.100"
      borderRadius="medium"
      boxShadow="elevation_2"
      paddingHorizontal={3}
      paddingVertical={2}
      minWidth={TOOLTIP_MIN_WIDTH}
    >
      <VStack spacing={2}>
        <Show when={isNotNil(label)}>
          <Text size="xSmall" alternative>
            {label}
          </Text>
        </Show>
        {items.map((item, index) => {
          const formatted = formatItem(item, index, items, props.formatter);
          return (
            <HStack
              key={String(item.dataKey ?? item.name)}
              spacing={4}
              justify="space-between"
              align="center"
            >
              <HStack spacing={2} align="center">
                <ChartSwatch color={swatchColorOf(item)} />
                <Text size="xSmall" color="secondary">
                  {formatted.name}
                </Text>
              </HStack>
              <Text size="xSmall" alternative>
                {formatted.value}
              </Text>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
}
